import WindowDimensions from '../../types/window-dimensions';
import ScrollPosition from '../../types/scroll-position';
import WindowPosition from '../../types/window-position';
import mitt, {Emitter} from 'mitt';
import {Tooltip} from 'bootstrap';
import LanguageUtility from './language-utility';

// Type for browser change events
type BrowserEvent = {
    resize: WindowDimensions,
    scroll: ScrollPosition,
    orientation: string
};

/**
 * Contains general utility methods.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class GeneralUtility {
    /**
     * Current page UID.
     */
    private static _pageUid: number;

    /**
     * Important page UIDs.
     */
    private static _pages: any;

    /**
     * Current page UID rootline.
     */
    private static _rootline: number[];

    /**
     * Emits browser related events.
     */
    private static _browserEmitter: Emitter<BrowserEvent> = mitt<BrowserEvent>();

    /**
     * Gets a property from the `Global` variable. If the property is not found, an error is thrown.
     *
     * @param {string} name Name of the property to get
     * @returns {any} Property value, if found
     */
    public static getGlobalProperty(name: string): any {
        if (Globals === undefined || !Globals) {
            throw new Error('Globals are missing.');
        }
        if (Globals[name] === undefined || !Globals[name] || !Globals.hasOwnProperty(name)) {
            throw new Error('Property ' + name + ' not found in globals.');
        }
        return Globals[name];
    }

    static get pageUid(): number {
        if (this._pageUid === undefined) {
            this._pageUid = parseInt(GeneralUtility.getGlobalProperty('pageUid'));
        }
        return this._pageUid;
    }

    static get pages(): any {
        if (this._pages === undefined) {
            this._pages = GeneralUtility.getGlobalProperty('pages');
        }
        return this._pages;
    }

    static get rootline(): number[] {
        if (this._rootline === undefined) {
            this._rootline = GeneralUtility.getGlobalProperty('rootline');
        }
        return this._rootline;
    }

    static get browserEmitter(): Emitter<BrowserEvent> {
        return this._browserEmitter;
    }

    /**
     * Gets the current page URL without any parameters or hashes.
     *
     * @returns {string} Current URL without a trailing slash
     */
    static getUrl(): string {
        let href = window.location.href;
        href = href.split('?')[0];
        href = href.split('#')[0];
        const length = href.length;

        if (href.charAt(length - 1) === '/') {
            href = href.substring(0, length - 1);
        }

        return href;
    }

    /**
     * Adds or replaces a URL parameter and reloads the page.
     *
     * @param {string} parameter Name of the parameter
     * @param {string} value Parameter value
     * @param {boolean} [clear] If true, then any existing URL parameters will be removed before adding the given parameter (defaults to false)
     */
    static addUrlParameter(parameter: string, value: string, clear: boolean = false): void {
        let href = window.location.href;

        if (clear) {
            const index = href.indexOf('?');
            if (index !== -1) {
                href = href.substring(0, index);
            }
        }

        const escapedParameter = parameter.trim()
            .replace('[', '\\[')
            .replace(']', '\\]')
            .replace('(', '\\(')
            .replace(')', '\\)')
            .replace('&', '\\&')
            .replace('?', '\\?');

        const escapedValue = value.trim();

        const regExp = new RegExp(`${escapedParameter}=.+?`);
        href = href.replace(regExp, '');
        if (href.endsWith('?')) {
            href = href.substring(0, href.length - 1);
        }
        href += (href.indexOf('?') === -1 ? '?' : '&') + `${parameter}=${escapedValue}`;
        window.location.href = href;
    }

    /**
     * Hides all tooltips
     */
    static hideAllTooltips(): void {
        document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(tooltip => {
            Tooltip.getOrCreateInstance(tooltip).hide();
        });
    }

    /**
     * Checks if the current devices is a touch device. Note that devices with multi-touch capable touchpads like laptops will also be detected as mobile.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#Mobile_Device_Detection
     */
    static isTouch(): boolean {
        let hasTouchScreen: boolean;
        if ('maxTouchPoints' in navigator) {
            hasTouchScreen = navigator.maxTouchPoints > 0;
        } else if ('msMaxTouchPoints' in navigator) {
            // @ts-ignore
            hasTouchScreen = navigator.msMaxTouchPoints > 0;
        } else {
            const mQ = window.matchMedia !== undefined && window.matchMedia('(pointer:coarse)');
            if (mQ && mQ.media === '(pointer:coarse)') {
                hasTouchScreen = !!mQ.matches;
            } else if ('orientation' in window) {
                hasTouchScreen = true; // deprecated, but good fallback
            } else {
                // Only as a last resort, fall back to user agent sniffing
                // @ts-ignore
                const UA = navigator.userAgent;
                hasTouchScreen = (
                    /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
                    /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
                );
            }
        }

        return hasTouchScreen;
    }

    /**
     * Gets the current device orientation.
     *
     * @returns {string} "portrait" or "landscape"
     */
    static getDeviceOrientation(): string {
        if (window.innerHeight > window.innerWidth) {
            return 'portrait';
        } else {
            return 'landscape';
        }
    }

    /**
     * Gets the current window dimensions.
     */
    static getWindowDimensions(): WindowDimensions {
        return {
            'width': Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
            'height': Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
        };
    }

    /**
     * Gets the current window scroll position.
     */
    static getScrollPosition(): WindowPosition {
        return {
            'top': document.documentElement.scrollTop || document.body.scrollTop,
            'left': document.documentElement.scrollLeft || document.body.scrollLeft
        };
    }

    /**
     * Gets the current position of an element within the window.
     *
     * @param {HTMLElement} element Element to get offset from
     */
    static getElementPosition(element: HTMLElement): WindowPosition {
        const boundaries = element.getBoundingClientRect();
        const scrollPosition = GeneralUtility.getScrollPosition();
        return {
            'top': boundaries.top + scrollPosition.top,
            'left': boundaries.left + scrollPosition.left
        };
    }

    /**
     * Adds a class to an selector for a certain amount of time, then removes it again.
     *
     * @param {HTMLElement} element Element to add class to
     * @param {string} newClass Class to add
     * @param {number} [period] Live-Time of the class in ms, defaults to 1000ms
     */
    static addTimedClass(element: HTMLElement, newClass: string, period: number = 1000) {
        element.classList.add(newClass);
        setTimeout(() => {
            element.classList.remove(newClass);
        }, period);
    }

    /**
     * Implements a debounced text-seach on the given text input field.
     *
     * @param {HTMLInputElement} inputField Input field to implement search for
     * @param {function} callback Callback when the search changes
     * @param {number} [debounceTimeout] Debounce-delay in milliseconds (defaults to 500)
     * @param {function} [parse] Function to parse the input field value by (defaults to lowercasing and trimming the string-value)
     */
    static debouncedSearch(inputField: HTMLInputElement, callback: any, debounceTimeout: number = 500, parse: any = null) {
        if (debounceTimeout === undefined) {
            debounceTimeout = 500;
        }

        let debounce: boolean = false;
        let timeout: any = null;

        /**
         * Checks if the given key is allowed.
         *
         * @param {number} keyCode Key-code to check
         * @returns {boolean} True when the key is allowed, otherwise false
         */
        const isAllowedKey = (keyCode: number): boolean => {
            const allowedKeyCodes: number[] = [
                8, // Backspace
                13, // Enter
                16, // Shift
                17, // Ctrl
                32, // Space
                46, // Delete
                106, // Multiply numpad
                109, // Minus numpad
                173, // Minus
                110, // Comma numpad
                188, // Comma
                59, // ü
                192, // ö
                222 // ä
            ];

            if (allowedKeyCodes.indexOf(keyCode) !== -1) {
                return true;
            }

            // 0-9
            else if (keyCode >= 48 && keyCode <= 57) {
                return true;
            }

            // 0-9 numpad
            else if (keyCode >= 96 && keyCode <= 105) {
                return true;
            }

            // a-z
            else if (keyCode >= 65 && keyCode <= 90) {
                return true;
            }

            return false;
        };

        inputField.addEventListener('keyup', (e) => {
            if (!isAllowedKey(e.keyCode) || debounce) {
                return;
            }

            if (timeout !== null) {
                clearTimeout(timeout);
            }

            timeout = setTimeout(() => {
                let value = inputField.value;

                if (parse) {
                    value = parse(value);
                } else {
                    value = value.toLowerCase().trim();
                }

                callback(value);

                debounce = false;
            }, debounceTimeout);

            debounce = true;
        });
    }

    /**
     * Creates a loading spinner.
     *
     * @param { [key: string]: any } configuration Configuration of the spinner
     * @param {string} [configuration.type] "spinner-grow" or "spinner-border" (defaults to "spinner-grow")
     * @param {string} [configuration.color] Any of the text-* CSS classes, e,g. text-primary or text-warning (defaults to "text-primary")
     * @param {string} [configuration.text] Loading text (defaults to "Loading...")
     * @param {number} [configuration.size] Size of the spinner in rem
     * @param { [key: string]: any } [configuration.overlay] If set, the spinner is displayed within an overlay
     * @param {boolean} [configuration.overlay.enabled] Whether the overlay is enabled or not (defaults to false)
     * @param {number} [configuration.overlay.opacity] Set CSS opacity of overlay (defaults to 0.75)
     * @param {boolean} [configuration.overlay.class] Additional container classes
     * @param {boolean} [configuration.overlay.color] CSS background-color value for the overlay
     * @param {boolean} [configuration.overlay.colorClass] Color CSS class for the overlay (defaults to bg-white)
     * @returns {HTMLElement} Spinner or spinner overlay containing the spinner
     */
    static createSpinner(configuration: { [key: string]: any }): HTMLElement {
        // Validate configuration
        if (!configuration.hasOwnProperty('type')) {
            configuration.type = 'spinner-grow';
        }

        if (!configuration.hasOwnProperty('color')) {
            configuration.color = 'text-primary';
        }

        if (!configuration.hasOwnProperty('text')) {
            configuration.text = LanguageUtility.getLabel('loading');
            if (configuration.text === undefined || !configuration.text) {
                configuration.text = 'Loading...';
            }
        }

        if (configuration.hasOwnProperty('size')) {
            const size: number = parseInt(configuration.size);
            if (isNaN(size)) {
                configuration.size = null;
            } else {
                configuration.size = size + 'rem';
            }
        }

        if (!configuration.hasOwnProperty('overlay')) {
            configuration.overlay = {};
            configuration.overlay.enabled = false;
        } else {
            configuration.overlay.enabled = true;

            if (!configuration.overlay.hasOwnProperty('opacity')) {
                configuration.overlay.opacity = 0.75;
            }

            if (!configuration.overlay.hasOwnProperty('class')) {
                configuration.overlay.class = 'd-flex justify-content-center';
            }

            if (!configuration.overlay.hasOwnProperty('color')) {
                configuration.overlay.color = null;
            }

            if (!configuration.overlay.hasOwnProperty('colorClass')) {
                configuration.overlay.colorClass = 'bg-white';
            }
        }

        // Create spinner
        const spinner = document.createElement('div');
        spinner.classList.add('spinner', 'align-self-center', configuration.type, configuration.color);

        if (configuration.size) {
            spinner.style.width = configuration.size;
            spinner.style.height = configuration.size;
        }

        if (configuration.text) {
            const srOnly = document.createElement('span');
            srOnly.classList.add('visually-hidden');
            srOnly.innerHTML = configuration.text;
            spinner.append(srOnly);
        }

        if (configuration.overlay.enabled) {
            const overlay = document.createElement('div');
            overlay.classList.add('spinner-overlay');
            overlay.append(spinner);

            const overlayColor = document.createElement('div');
            overlayColor.classList.add('overlay-color');
            overlay.append(overlayColor);

            if (configuration.overlay.opacity !== null) {
                overlayColor.style.opacity = configuration.overlay.opacity;
            }

            if (configuration.overlay.class) {
                overlay.classList.add(...configuration.overlay.class.split(' '));
            }

            if (configuration.overlay.color) {
                overlayColor.style.backgroundColor = configuration.overlay.color;
            }

            if (configuration.overlay.colorClass) {
                overlayColor.classList.add(configuration.overlay.colorClass);
            }

            return overlay;
        }

        return spinner;
    }

    /**
     * Copies text to the clients clipboard.
     *
     * @param {string} text Text to copy to the clipboard
     */
    static copyToClipboard(text: string): void {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
        }
        // Fallback for older browsers
        else {
            const textarea = document.createElement('textarea');
            textarea.style.top = '0';
            textarea.style.left = '0';
            textarea.style.position = 'fixed';
            textarea.value = text;

            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            try {
                const success = document.execCommand('copy');
                if (!success) {
                    throw new Error('Copying to clipboard failed.');
                }
            } catch (error) {
                throw new Error('Browser does not support clipboard interaction.');
            }

            textarea.remove();
        }
    }
}
